import { Injectable, computed, inject } from '@angular/core';
import { MenuService } from '../menu/menu.service';

export interface ComboGroupDef {
  groupId: string;
  name: string;
  requiredSubIds: string[];
  optionalSubIds: string[];
  subNames: Record<string, string>;
}

export interface ComboSlotRef {
  groupId: string;
  subId: string;
  isOptional: boolean;
}

interface ComboRegistry {
  groups: Map<string, ComboGroupDef>;
  slotIndex: Map<string, ComboSlotRef>;
}

// A combo group is a top-level section with at least one subsection marked
// `required` - the same rule the old per-section combo mode used to decide
// "is this a combo", just resolved once for the whole menu instead of being
// rebuilt from whichever section the guest happens to be viewing.
@Injectable({ providedIn: 'root' })
export class ComboRegistryService {
  private readonly menuService = inject(MenuService);

  private readonly registry = computed<ComboRegistry>(() => {
    const groups = new Map<string, ComboGroupDef>();
    const slotIndex = new Map<string, ComboSlotRef>();

    for (const section of this.menuService.menu()?.sections ?? []) {
      const subs = section.sub_sections ?? [];
      const requiredSubIds = subs.filter(sub => sub.required).map(sub => sub.id);
      if (!requiredSubIds.length) continue;

      const optionalSubIds = subs.filter(sub => !sub.required).map(sub => sub.id);
      const subNames = Object.fromEntries(subs.map(sub => [sub.id, sub.name]));
      groups.set(section.id, { groupId: section.id, name: section.name, requiredSubIds, optionalSubIds, subNames });

      for (const sub of subs) {
        const isOptional = !sub.required;
        for (const item of sub.items ?? []) {
          slotIndex.set(this.key(item.id, sub.id), { groupId: section.id, subId: sub.id, isOptional });
        }
      }
    }

    return { groups, slotIndex };
  });

  getGroups(): ComboGroupDef[] {
    return Array.from(this.registry().groups.values());
  }

  resolveLine(itemId: string, subSectionId: string): ComboSlotRef | undefined {
    return this.registry().slotIndex.get(this.key(itemId, subSectionId));
  }

  private key(itemId: string, subSectionId: string): string {
    return `${itemId}::${subSectionId}`;
  }
}
